import React, { useState, useEffect, useRef } from "react";
import { Pin, PinOff, EyeOff, Eye, Trash2, Shield, Library } from "lucide-react";
import { useAdmin } from "../shared/AdminContext";
import { adminDeletePost, adminPinPost, adminHidePost } from "../utils/communityApi";
import { supabase } from "../../supabaseClient";
import { bamConfirm, bamToast } from "../shared/dialog";
import { GOLD } from "../constants/theme";

// Inline admin menu rendered on each community post. Returns null unless the
// viewer is in admin mode; clicking opens a small popover with
// Delete / Pin / Hide actions that call the SECURITY DEFINER RPCs.
export function PostAdminMenu({ post, onChanged, C, dark }) {
  const { isAdmin, adminMode } = useAdmin();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!isAdmin || !adminMode) return null;

  const pinned = !!post.pinnedAt;
  const hidden = !!post.hiddenAt;
  const video = (Array.isArray(post.embeds) ? post.embeds : []).find(e => e?.type === "video" && e.url);

  // The post's clip into the upload queue, credited to the author. Review
  // happens in Admin, Content, Pending Uploads like any submission.
  const addToLibrary = () => run(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch("/api/admin-actions?action=import-post-video", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
      body: JSON.stringify({ postId: post.id, url: video.url }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
    bamToast(j.already ? "Already in the queue." : "Sent to Pending Uploads, credited to the author.");
  });

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Action failed", { tone: "error" });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const onDelete = async () => {
    setOpen(false);
 if (!(await bamConfirm({ title: "Delete this post?", message: "Admin delete: this removes it from the feed for everyone.", confirmLabel: "Delete", danger: true }))) return;
    run(() => adminDeletePost(post.id));
  };

  return (
    <div ref={ref} style={{position:"relative",flexShrink:0}}>
      <button
        title="Admin actions"
        onClick={(e)=>{e.stopPropagation();setOpen(o=>!o);}}
        style={{width:28,height:28,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",
          background:open?`${GOLD}18`:"transparent",border:`1px solid ${open?GOLD+"55":"transparent"}`,cursor:"pointer"}}>
        <Shield size={13} color={GOLD}/>
      </button>
      {open && (
        <div onClick={e=>e.stopPropagation()}
          style={{position:"absolute",top:"calc(100% + 6px)",right:0,zIndex:100,minWidth:180,
            background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,overflow:"hidden",
            boxShadow:dark?"0 12px 40px rgba(0,0,0,0.55)":"0 8px 24px rgba(0,0,0,0.12)"}}>
          <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",padding:"10px 14px 6px"}}>Admin</div>
          <MenuItem C={C} icon={pinned ? <PinOff size={13} color={C.textMid}/> : <Pin size={13} color={GOLD}/>}
            label={pinned ? "Unpin" : "Pin to top"}
            onClick={()=>run(()=>adminPinPost(post.id, !pinned))}/>
          <MenuItem C={C} icon={hidden ? <Eye size={13} color={C.textMid}/> : <EyeOff size={13} color={C.textMid}/>}
            label={hidden ? "Unhide from feed" : "Hide from feed"}
            onClick={()=>run(()=>adminHidePost(post.id, !hidden))}/>
          {video && (
            <MenuItem C={C} icon={<Library size={13} color={GOLD}/>}
              label="Add to drill library"
              onClick={addToLibrary}/>
          )}
          <div style={{height:1,background:C.border,margin:"4px 0"}}/>
          <MenuItem C={C} icon={<Trash2 size={13} color="#F87171"/>}
            label="Delete post"
            danger
            onClick={onDelete}/>
        </div>
      )}
    </div>
  );
}

function MenuItem({ C, icon, label, onClick, danger }) {
  return (
    <div onClick={onClick}
      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",fontSize:13,
        color:danger?"#F87171":C.text,cursor:"pointer",userSelect:"none",transition:"background .12s"}}
      onMouseEnter={(e)=>{e.currentTarget.style.background = danger?"rgba(248,113,113,0.08)":"rgba(226,221,159,0.08)";}}
      onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";}}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

export default PostAdminMenu;
